import { useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchDashboardStats } from '../api/admin';
import { RenewLicenseDialog } from '../components/RenewLicenseDialog';
import { Snackbar } from '@mui/material';

export function Dashboard() {
    const queryClient = useQueryClient();
    const [renewDialogOpen, setRenewDialogOpen] = useState(false);
    const [licenseToRenew, setLicenseToRenew] = useState<any>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const showMessage = (msg: string) => {
        setSnackbarMessage(msg);
        setSnackbarOpen(true);
    };

    const { data, isLoading, error } = useQuery({
        queryKey: ['dashboard-stats'],
        queryFn: fetchDashboardStats,
        refetchInterval: 30000, // Refetch every 30 seconds
        placeholderData: keepPreviousData,
    });

    if (isLoading) {
        return <div className="text-center py-8 text-gray-500">Loading dashboard...</div>;
    }

    if (error) {
        return <div className="text-center py-8 text-red-500">Error loading dashboard</div>;
    }

    const stats = data || {};

    return (
        <div className="max-w-6xl mx-auto space-y-8">
            <div className="pl-1">
                <div className="text-[10px] tracking-[0.25em] text-[#c87a53] uppercase font-bold mb-1">
                    OVERVIEW
                </div>
                <h1 className="text-3xl font-extrabold text-[#0b1329] dark:text-white tracking-tight">
                    System posture
                </h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    High-signal metrics for licensing traffic, product catalogs, and server expirations.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5">
                <StatCard
                    title="Total Products"
                    value={stats.totalProducts || 0}
                    icon="📦"
                    bgClass="bg-slate-100/90 dark:bg-slate-900/40"
                    titleColor="text-slate-500 dark:text-slate-400"
                    valColor="text-slate-800 dark:text-slate-100"
                />
                <StatCard
                    title="Total Licenses"
                    value={stats.totalLicenses || 0}
                    icon="🎫"
                    bgClass="bg-[#fef3c7] dark:bg-amber-950/20"
                    titleColor="text-amber-700 dark:text-amber-400"
                    valColor="text-[#9a3412] dark:text-[#fb923c]"
                />
                <StatCard
                    title="Active Machines"
                    value={stats.activeServers || 0}
                    icon="🖥️"
                    bgClass="bg-[#dcfce7] dark:bg-emerald-950/20"
                    titleColor="text-emerald-700 dark:text-emerald-400"
                    valColor="text-[#15803d] dark:text-[#4ade80]"
                />
                <StatCard
                    title="Suspicious"
                    value={stats.suspiciousServers || 0}
                    icon="🧐"
                    bgClass="bg-[#ffedd5] dark:bg-orange-950/20"
                    titleColor="text-orange-700 dark:text-orange-400"
                    valColor="text-[#c2410c] dark:text-[#fb923c]"
                />
                <StatCard
                    title="Blocked"
                    value={stats.blockedServers || 0}
                    icon="🚫"
                    bgClass="bg-[#ffe4e6] dark:bg-rose-950/20"
                    titleColor="text-red-700 dark:text-red-400"
                    valColor="text-[#be123c] dark:text-[#f43f5e]"
                />
                <StatCard
                    title="Expiring Soon"
                    value={stats.expiringLicenses || 0}
                    icon="⚠️"
                    bgClass="bg-[#fee2e2] dark:bg-rose-950/20"
                    titleColor="text-rose-700 dark:text-rose-400"
                    valColor="text-[#b91c1c] dark:text-[#f87171]"
                />
            </div>

            {stats.expiringLicensesList && stats.expiringLicensesList.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150 dark:border-gray-700/60 p-6">
                    <div className="mb-4 pl-1">
                        <div className="text-[10px] tracking-[0.25em] text-[#c87a53] uppercase font-bold mb-1">
                            WARNINGS
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Licenses Expiring Soon
                        </h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                        License Key
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                        Customer
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                        Expires At
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                        Days Remaining
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                        Machine Count
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {stats.expiringLicensesList.map((license: any) => (
                                    <tr key={license.id}>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-mono">
                                            {license.licenseKey}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-950 dark:text-gray-200 whitespace-nowrap font-medium">
                                            {license.customerId ? (
                                                <Link 
                                                    to={`/customers/${license.customerId}`} 
                                                    className="text-orange-600 dark:text-orange-400 hover:underline"
                                                >
                                                    {license.customerName || 'Unknown Customer'}
                                                </Link>
                                            ) : (
                                                <span className="text-gray-400 italic">None</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-mono text-xs">
                                            {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'Never'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap">
                                            {(() => {
                                                const diffTime = (license.expiresAt || 0) - Date.now();
                                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                                return (
                                                    <span className={`font-semibold px-2 py-0.5 rounded text-xs ${diffDays <= 7 ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200' : 'bg-orange-100 text-orange-850 dark:bg-orange-900/30 dark:text-orange-200'}`}>
                                                        {diffDays > 0 ? `${diffDays} days` : 'Expired'}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-semibold">
                                            {license.activatedServers?.length || 0}
                                        </td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    setLicenseToRenew(license);
                                                    setRenewDialogOpen(true);
                                                }}
                                                className="px-3 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 font-medium transition-colors cursor-pointer"
                                            >
                                                Renew
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Shared Renew License Dialog */}
            <RenewLicenseDialog
                open={renewDialogOpen}
                onClose={() => setRenewDialogOpen(false)}
                license={licenseToRenew}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
                    showMessage('License renewed successfully');
                    setRenewDialogOpen(false);
                }}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </div>
    );
}

interface StatCardProps {
    title: string;
    value: number;
    icon: string;
    bgClass: string;
    titleColor: string;
    valColor: string;
}

function StatCard({ title, value, icon, bgClass, titleColor, valColor }: StatCardProps) {
    return (
        <div className={`${bgClass} rounded-3xl p-6 shadow-sm border border-gray-150/10 transition duration-150 hover:shadow-md flex flex-col justify-between min-h-[140px]`}>
            <div>
                <p className={`text-[10px] font-bold tracking-widest ${titleColor} uppercase mb-2`}>{title}</p>
                <p className={`text-4xl font-extrabold ${valColor} tracking-tight`}>{value}</p>
            </div>
            <div className="flex justify-between items-center mt-4 pt-2 border-t border-black/5 dark:border-white/5">
                <span className="text-[10px] uppercase font-bold text-gray-400/80 dark:text-gray-500">Live Metric</span>
                <span className="text-base filter drop-shadow">{icon}</span>
            </div>
        </div>
    );
}